/**
 * Recharge Buy Box Component
 * Integrates with Recharge subscription widget via shadow DOM
 */
class RechargeBuyBox extends HTMLElement {
  constructor() {
    super();

    // Configuration
    this.CONFIG = {
      WIDGET_SELECTOR: "recharge-subscription-widget",
      DELAY_MS: 100,
    };

    // State
    this.productId = this.dataset.productId;
    this.defaultVariantId = this.dataset.defaultVariantId;
    this.rechargeWidget = null;
    this.isInitialized = false;
    this.isHandlingVariantChange = false;
    this.variantChangeTimeout = null;
    this.lastRenderedVariantId = null;
    this.badge = this.dataset.optionBadge || null;
    this.badgeLabel = this.dataset.optionBadgeLabel || null;
    this.badgeColor = this.dataset.optionBadgeColor || null;

    this.multiBadges = this.dataset.multiBadges === 'true';
    this.multiBadgeConfig = {
      monthly: this.multiBadges && this.dataset.multiBadgeMonthlyText
        ? { text: this.dataset.multiBadgeMonthlyText, color: this.dataset.multiBadgeMonthlyColor || '#FF3D00' }
        : null,
      annual: this.multiBadges && this.dataset.multiBadgeAnnualText
        ? { text: this.dataset.multiBadgeAnnualText, color: this.dataset.multiBadgeAnnualColor || '#FF3D00' }
        : null,
    };

    // Elements - will be queried after rendering
    this.optionButtons = [];

    // Product data
    this.productData = this.parseProductData();
    this.unitType = this.dataset.unitType || 'pouch';

    // Shipment configuration (from product metafields, defaults to US values)
    this.shipmentFrequencyMonths = parseInt(this.dataset.shipmentFrequencyMonths) || 1; // Months between shipments (US: 1, CA: 2)
    this.itemsPerShipment = parseInt(this.dataset.itemsPerShipment) || 1; // Items per shipment (US: 1, CA: 2)
  }

  connectedCallback() {
    this.init();
  }

  parseProductData() {
    const productDataEl = this.querySelector('[data-product-data]');

    if (productDataEl) {
      try {
        const data = JSON.parse(productDataEl.textContent);
        return data;
      } catch (e) {
        console.error('[RechargeBuyBox] Failed to parse product data:', e);
      }
    }
    return null;
  }

  /**
   * Get the currently selected variant ID from various sources
   */
  getCurrentVariantId() {
    // Try URL parameter first (updates fastest during variant changes)
    const urlParams = new URLSearchParams(window.location.search);
    const variantParam = urlParams.get('variant');
    if (variantParam) {
      return variantParam;
    }

    // Try to find product form with variant input
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        return variantInput.value;
      }
    }

    // Try Recharge widget (reliable but may update slower)
    if (this.rechargeWidget) {
      const variantId = this.rechargeWidget.getAttribute('default-variant-id');
      if (variantId) {
        return variantId;
      }
    }

    // Fallback to default variant ID from our data attribute
    const defaultId = this.dataset.defaultVariantId;
    return defaultId;
  }

  /**
   * Get selling plan order from Recharge widget
   */
  getRechargeSellingPlanOrder() {
    if (!this.rechargeWidget || !this.rechargeWidget.shadowRoot) {
      return null;
    }

    try {
      const shadowRoot = this.rechargeWidget.shadowRoot;
      const sellingPlansEl = shadowRoot.querySelector('rc-selling-plans');

      if (sellingPlansEl && sellingPlansEl.shadowRoot) {
        const planInputs = sellingPlansEl.shadowRoot.querySelectorAll('input[name="rc_plan"]');
        const order = Array.from(planInputs).map(input => input.value);
        return order;
      }
    } catch (error) {
      console.error('[RechargeBuyBox] Error getting Recharge plan order:', error);
    }

    return null;
  }

  /**
   * Render all purchase options (subscription + one-time) from product data
   */
  renderSubscriptionOptions(variantId = null) {

    if (!this.productData) {
      console.error('[RechargeBuyBox] No product data available');
      return;
    }

    const container = this.querySelector('[data-all-options]');
    if (!container) {
      console.error('[RechargeBuyBox] Options container not found');
      return;
    }

    // Get current variant ID if not provided
    if (!variantId) {
      variantId = this.getCurrentVariantId();
    }

    // Find the specific variant in our product data
    let currentVariant = this.productData.variants.find(v => v.id.toString() === variantId.toString());

    // Fallback to first variant if not found
    if (!currentVariant) {
      currentVariant = this.productData.variants[0];
    }

    // Track which variant we're rendering
    this.lastRenderedVariantId = currentVariant.id.toString();

    // Clear container
    container.innerHTML = '';

    // Base price (individual/one-time purchase) for calculations
    const basePrice = currentVariant.price; // in cents

    // Get the order from Recharge widget
    const rechargeOrder = this.getRechargeSellingPlanOrder();

    // Build a list of all valid plans for this variant
    const validPlans = [];
    const sellingPlanGroups = this.productData.selling_plan_groups;

    if (sellingPlanGroups && sellingPlanGroups.length > 0) {
      sellingPlanGroups.forEach((group) => {
        group.selling_plans.forEach((plan) => {
          // Find the selling plan allocation for this variant
          const allocation = currentVariant.selling_plan_allocations.find(
            (alloc) => alloc.selling_plan_id === plan.id
          );

          if (allocation) {
            validPlans.push({
              group,
              plan,
              allocation
            });
          }
        });
      });
    }

    // Sort plans according to Recharge widget order
    if (rechargeOrder && rechargeOrder.length > 0) {
      validPlans.sort((a, b) => {
        const aIndex = rechargeOrder.indexOf(a.plan.id.toString());
        const bIndex = rechargeOrder.indexOf(b.plan.id.toString());

        // If both found, sort by index
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }
        // If only one found, prioritize it
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        // If neither found, maintain original order
        return 0;
      });
    }

    // overrides will be pulled from window.recharge_options_overrides if available
    const options_overrides = window.recharge_options_overrides || {};

    // Render sorted plans
    validPlans.forEach(({ group, plan, allocation }) => {
      // console.log(`[RechargeBuyBox] Rendering plan:`, plan.name);

      const totalPrice = allocation.price;
      let comparePrice = null;
      const groupNameLower = group.name.toLowerCase();
      const isAnnual = groupNameLower.includes('annual') || groupNameLower.includes('1 year');
      const isSemiAnnual = groupNameLower.includes('semi-annual') || groupNameLower.includes('6 month');
      const isMonthly = groupNameLower.includes('monthly');

      if (isSemiAnnual && this.productData.price_max) {
        comparePrice = this.productData.price_max * 6;
      } else if (isAnnual && this.productData.price_max) {
        comparePrice = this.productData.price_max * 12;
      } else if (isMonthly && this.productData.price_max) {
        comparePrice = this.productData.price_max;
      }

      // Determine number of bottles and supply text
      let numBottles = 1;
      let supplyText = '';
      let deliveryText = '';
      if (isSemiAnnual) {
        numBottles = 6;
        supplyText = '6-month supply';
        deliveryText = ` • 1 ${this.unitType} delivered each month`;
      } else if (isAnnual) {
        numBottles = 12;
        supplyText = '12-month supply';
        deliveryText = ` • 1 ${this.unitType} delivered each month`;
      } else if (isMonthly) {
        numBottles = 1;
        supplyText = '1 month supply';
        deliveryText = ` • 1 ${this.unitType} delivered each month`;
      }

      // Calculate delivery text based on product metafield settings
      const itemsPerShipment = this.itemsPerShipment;
      const frequencyMonths = this.shipmentFrequencyMonths;
      const unitDisplay = itemsPerShipment > 1 ? this.pluralizeUnit(this.unitType) : this.unitType;

      if (frequencyMonths === 1) {
        // Monthly shipments (US default)
        deliveryText = ` • ${itemsPerShipment} ${unitDisplay} delivered each month`;
      } else if (frequencyMonths === 2) {
        // Bi-monthly shipments (CA)
        deliveryText = ` • ${itemsPerShipment} ${unitDisplay} delivered every 2 months`;
      } else {
        // Generic fallback
        deliveryText = ` • ${itemsPerShipment} ${unitDisplay} delivered every ${frequencyMonths} months`;
      }

      // Calculate per bottle price
      const pricePerBottle = Math.ceil(totalPrice / numBottles);

      // Calculate original price (what they would pay without subscription)
      const originalPrice = basePrice * numBottles;

      // Calculate savings
      const savingsPerBottle = basePrice - pricePerBottle;
      const savingsPercent = Math.round((savingsPerBottle / basePrice) * 100);

      // console.log(`[RechargeBuyBox] Creating subscription button for ${plan.name}`);
      // console.log(`[RechargeBuyBox] Calculations: bottles=${numBottles}, perBottle=${pricePerBottle}, savings=${savingsPercent}%, original=${originalPrice}, compareAt=${comparePrice}`);

      // Create option button
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'recharge-buy-box__option';
      button.dataset.purchaseType = 'subscription';
      button.dataset.variantId = currentVariant.id;
      button.dataset.sellingPlanId = plan.id;
      button.dataset.sellingPlanName = group.name;
      button.dataset.price = totalPrice;

      // Determine canonical option name for badge matching and overrides
      let option_name = 'monthly'; // default
      if (isSemiAnnual) {
        option_name = 'semiannual';
      } else if (isAnnual) {
        option_name = 'annual';
      } else if (isMonthly) {
        option_name = 'monthly';
      }

      // Determine badge for this option. Multi-badge mode (DNA Day style)
      // takes precedence over the single-badge setting when enabled.
      let shouldShowBadge = false;
      let optionBadgeLabel = null;
      let optionBadgeColor = null;
      if (this.multiBadges) {
        let cfg = null;
        if (isMonthly) cfg = this.multiBadgeConfig.monthly;
        else if (isAnnual && !isSemiAnnual) cfg = this.multiBadgeConfig.annual;
        if (cfg) {
          shouldShowBadge = true;
          optionBadgeLabel = cfg.text;
          optionBadgeColor = cfg.color;
        }
      } else if (this.badge) {
        const badgeLower = this.badge.toLowerCase();
        shouldShowBadge = (
          (badgeLower === 'annual' && isAnnual && !isSemiAnnual) ||
          (badgeLower === 'semiannual' && isSemiAnnual) ||
          (badgeLower === 'monthly' && isMonthly)
        );
        if (shouldShowBadge) {
          optionBadgeLabel = this.badgeLabel;
          optionBadgeColor = this.badgeColor;
        }
      }
      if (shouldShowBadge) {
        button.classList.add('recharge-buy-box__option--badge');
      }

      const use_overrides = options_overrides[`use_overrides_for_${option_name}`] === 'true';

      // Helper to check if an override value is set (not empty string or falsy)
      const hasOverride = (key) => use_overrides && options_overrides[key] && options_overrides[key] !== '';
      const getNumericOverride = (key, fallback) => {
        const val = hasOverride(key) ? parseInt(options_overrides[key]) : NaN;
        return !isNaN(val) && val > 0 ? val : fallback;
      };
      const getTextOverride = (key, fallback) => hasOverride(key) ? options_overrides[key] : fallback;

      const option_price = getNumericOverride(`${option_name}_price`, totalPrice);
      const option_comparePrice = getNumericOverride(`${option_name}_compare_at_price`, comparePrice);
      const option_comparePrice2 = getNumericOverride(`${option_name}_compare_at_price_2`, null);
      const option_groupName = getTextOverride(`${option_name}_title`, group.name);
      const option_supplyText = getTextOverride(`${option_name}_subtitle`, supplyText + (deliveryText ? deliveryText : ''));
      const option_savingsText = getTextOverride(`${option_name}_savings`, `Save ${savingsPercent}%`);
      const option_savingsStrikethrough = getTextOverride(`${option_name}_savings_strikethrough`, null);
      const option_perBottleText = getTextOverride(`${option_name}_per`, `${this.formatPrice(pricePerBottle)} per ${this.unitType}`);

      // Build savings HTML with optional strikethrough
      let savingsHtml = '';
      if (option_savingsStrikethrough) {
        savingsHtml = `<span class="recharge-buy-box__option-savings"><span class="recharge-buy-box__option-savings--strikethrough">${option_savingsStrikethrough}</span> ${option_savingsText}</span>`;
      } else {
        savingsHtml = `<span class="recharge-buy-box__option-savings">${option_savingsText}</span>`;
      }

      button.innerHTML = `
        <span class="recharge-buy-box__radio"></span>
        <div class="recharge-buy-box__option-layout">
          <div class="recharge-buy-box__option-price-column">
            <span class="recharge-buy-box__option-price">${this.formatPrice(option_price)}</span>
            ${option_comparePrice ? `<span class="recharge-buy-box__option-compare-price">${this.formatPrice(option_comparePrice)}</span>` : ''}
            ${option_comparePrice2 ? `<span class="recharge-buy-box__option-compare-price-2">${this.formatPrice(option_comparePrice2)}</span>` : ''}
          </div>
          <div class="recharge-buy-box__option-info-column">
            <span class="recharge-buy-box__option-title">${option_groupName}</span>
            <span class="recharge-buy-box__option-subtitle">${option_supplyText}</span>
          </div>
          <div class="recharge-buy-box__option-savings-column">
            ${savingsHtml}
            <span class="recharge-buy-box__option-per-bottle">${option_perBottleText}</span>
          </div>
        </div>
        ${shouldShowBadge ? `<span class="recharge-buy-box__option-badge" style="--badge-color: ${optionBadgeColor || 'var(--c__brand-vermillion, #FF3D00)'};">${optionBadgeLabel || ''}</span>` : ''}
      `;

      container.appendChild(button);
    });

    // Render one-time purchase option (Individual)
    const onetimeButton = document.createElement('button');
    onetimeButton.type = 'button';
    onetimeButton.className = 'recharge-buy-box__option';
    onetimeButton.dataset.purchaseType = 'onetime';
    onetimeButton.dataset.variantId = currentVariant.id;
    onetimeButton.dataset.price = currentVariant.price;

    if (!this.multiBadges && this.badge?.toLowerCase() === 'individual') {
      onetimeButton.classList.add('recharge-buy-box__option--badge');
    }

    const individualSupplyMonths = this.itemsPerShipment;
    let supplyText = `${individualSupplyMonths}-month supply`;
    let deliveryText = `${this.itemsPerShipment} ${this.itemsPerShipment > 1 ? this.pluralizeUnit(this.unitType) : this.unitType} per order`;

    const use_overrides = options_overrides[`use_overrides_for_individual`] === 'true';

    // Helper to check if an override value is set (not empty string or falsy)
    const hasOverride = (key) => use_overrides && options_overrides[key] && options_overrides[key] !== '';
    const getNumericOverride = (key, fallback) => {
      const val = hasOverride(key) ? parseInt(options_overrides[key]) : NaN;
      return !isNaN(val) && val > 0 ? val : fallback;
    };
    const getTextOverride = (key, fallback) => hasOverride(key) ? options_overrides[key] : fallback;

    const option_price = getNumericOverride('individual_price', currentVariant.price);
    const option_comparePrice = getNumericOverride('individual_compare_at_price', null);
    const option_comparePrice2 = getNumericOverride('individual_compare_at_price_2', null);
    const option_groupName = getTextOverride('individual_title', 'Individual');
    const option_supplyText = getTextOverride('individual_subtitle', supplyText);
    const option_savingsText = getTextOverride('individual_savings', null);
    const option_savingsStrikethrough = getTextOverride('individual_savings_strikethrough', null);
    // Calculate per-bottle price for individual option when multiple items per shipment
    const individualPerBottlePrice = this.itemsPerShipment > 1 ? Math.ceil(currentVariant.price / this.itemsPerShipment) : null;
    const defaultPerBottleText = individualPerBottlePrice ? `${this.formatPrice(individualPerBottlePrice)} per ${this.unitType}` : null;
    const option_perBottleText = getTextOverride('individual_per', defaultPerBottleText);

    // Build savings HTML with optional strikethrough for individual option
    let individualSavingsHtml = '';
    if (option_savingsText) {
      if (option_savingsStrikethrough) {
        individualSavingsHtml = `<span class="recharge-buy-box__option-savings"><span class="recharge-buy-box__option-savings--strikethrough">${option_savingsStrikethrough}</span> ${option_savingsText}</span>`;
      } else {
        individualSavingsHtml = `<span class="recharge-buy-box__option-savings">${option_savingsText}</span>`;
      }
    }

    onetimeButton.innerHTML = `
      <span class="recharge-buy-box__radio"></span>
      <div class="recharge-buy-box__option-layout">
        <div class="recharge-buy-box__option-price-column">
          <span class="recharge-buy-box__option-price">${this.formatPrice(option_price)}</span>
          ${option_comparePrice ? `<span class="recharge-buy-box__option-compare-price">${this.formatPrice(option_comparePrice)}</span>` : ''}
          ${option_comparePrice2 ? `<span class="recharge-buy-box__option-compare-price-2">${this.formatPrice(option_comparePrice2)}</span>` : ''}
        </div>
        <div class="recharge-buy-box__option-info-column">
          <span class="recharge-buy-box__option-title">${option_groupName}</span>
          <span class="recharge-buy-box__option-subtitle">${option_supplyText}</span>
        </div>
        <div class="recharge-buy-box__option-savings-column">
            ${individualSavingsHtml}
            ${option_perBottleText ? `<span class="recharge-buy-box__option-per-bottle">${option_perBottleText}</span>` : ''}
        </div>
      </div>
      ${!this.multiBadges && this.badge && 'individual'.includes(this.badge.toLowerCase()) ? `<span class="recharge-buy-box__option-badge" style="--badge-color: ${this.badgeColor || 'var(--c__brand-vermillion, #FF3D00)'};">${this.badgeLabel || ''}</span>` : ''}
    `;

    container.appendChild(onetimeButton);
  }

  async init() {
    const hasSellingPlans = this.productData?.selling_plan_groups?.length > 0;

    if (hasSellingPlans) {
      // Only wait for Recharge widget if product has subscription plans
      this.rechargeWidget = await this.waitForRechargeWidget();

      if (!this.rechargeWidget) {
        console.error('[RechargeBuyBox] Recharge widget not found - cannot initialize');
        return;
      }
    }

    // Render options (subscription + one-time, or just one-time)
    this.renderSubscriptionOptions();

    // Re-query option buttons after rendering
    this.optionButtons = this.querySelectorAll('.recharge-buy-box__option');

    // Set up event listeners
    this.setupEventListeners();

    // Initialize default selection
    this.initializeDefaultSelection();

    // Listen for changes in Recharge widget (only if widget exists)
    if (this.rechargeWidget) {
      this.observeRechargeWidget();
    }

    // Listen for variant changes (always - works without Recharge widget)
    this.observeVariantChanges();

    this.isInitialized = true;

    // Fade in the buy box after everything is loaded
    requestAnimationFrame(() => {
      this.classList.add('recharge-buy-box--loaded');
    });
  }

  /**
   * Wait for Recharge widget to be available in the DOM
   */
  async waitForRechargeWidget() {

    return new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 100; // 10 seconds max wait

      const checkWidget = () => {
        attempts++;
        const widget = document.querySelector(this.CONFIG.WIDGET_SELECTOR);
        const shadowRoot = widget?.shadowRoot?.querySelector('rc-selling-plans');

        // console.log(`[RechargeBuyBox] Attempt ${attempts}/${maxAttempts} - Widget:`, !!widget, 'Shadow Root:', !!shadowRoot);

        if (widget && shadowRoot) {
          resolve(widget);
        } else if (attempts >= maxAttempts) {
          console.error('[RechargeBuyBox] ✗ Max attempts reached - widget not found');
          resolve(null);
        } else {
          setTimeout(checkWidget, this.CONFIG.DELAY_MS);
        }
      };

      checkWidget();
    });
  }

  /**
   * Set up event listeners for buy box interactions
   */
  setupEventListeners() {

    // Option button clicks
    this.optionButtons.forEach((button, index) => {
      // console.log(`[RechargeBuyBox] Adding click listener to option button ${index}:`, button.dataset.sellingPlanName || 'One-time');
      button.addEventListener('click', (e) => {
        this.handleOptionClick(e);
      });
    });
  }

  /**
   * Initialize default selection (from URL or first option)
   */
  initializeDefaultSelection() {
    // Check for URL query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const planParam = urlParams.get('plan');

    let selectedOption = null;

    if (planParam) {
      // Try to find option by selling plan ID
      selectedOption = this.querySelector(`[data-selling-plan-id="${planParam}"]`);

      // If not found by ID, try by purchase type (onetime/individual)
      if (!selectedOption && planParam === 'onetime') {
        selectedOption = this.querySelector('[data-purchase-type="onetime"]');
      }
    }

    // Default to first option if no URL param or option not found
    if (!selectedOption) {
      selectedOption = this.querySelector('.recharge-buy-box__option');
    }

    if (selectedOption) {
      selectedOption.classList.add('recharge-buy-box__option--active');

      // Update product heading pricing for initial selection
      this.updateProductHeadingPricing(selectedOption);

      // Sync with Recharge widget (only if widget exists)
      if (this.rechargeWidget) {
        const purchaseType = selectedOption.dataset.purchaseType;
        if (purchaseType === 'subscription') {
          const sellingPlanId = selectedOption.dataset.sellingPlanId;
          this.syncToRechargeWidget('subscription', sellingPlanId);
        } else {
          this.syncToRechargeWidget('onetime');
        }
      }
    }
  }

  /**
   * Handle option button clicks
   */
  handleOptionClick(event) {
    const option = event.currentTarget;
    const purchaseType = option.dataset.purchaseType;

    // Deselect all options
    this.optionButtons.forEach((btn) => {
      btn.classList.remove('recharge-buy-box__option--active');
    });

    // Select this option
    option.classList.add('recharge-buy-box__option--active');

    // Update URL query parameter
    this.updateUrlParameter(option);

    // Update product heading pricing
    this.updateProductHeadingPricing(option);

    // Sync with Recharge widget (only if widget exists)
    if (this.rechargeWidget) {
      if (purchaseType === 'subscription') {
        const sellingPlanId = option.dataset.sellingPlanId;
        this.syncToRechargeWidget('subscription', sellingPlanId);
      } else {
        this.syncToRechargeWidget('onetime');
      }
    }
  }

  /**
   * Update product heading pricing based on selected option
   */
  updateProductHeadingPricing(option) {
    const pricingColumn = document.querySelector('.product-heading__pricing-column');
    const priceElement = document.querySelector('.product-heading__price');
    const perUnitElement = document.querySelector('.product-heading__per-unit');
    const bestDealLabel = document.querySelector('.best-deal__label');

    if (!priceElement || !pricingColumn) return;

    let priceToShow = '';

    // Get per-bottle price from the option (for subscriptions)
    const perBottleElement = option.querySelector('.recharge-buy-box__option-per-bottle');

    if (perBottleElement) {
      // Subscription: extract per-bottle price (e.g., "$40" from "$40 per bottle")
      const perBottleText = perBottleElement.textContent || '';
      priceToShow = perBottleText.split(' ')[0]; // Gets "$40"
    } else {
      // One-time purchase: use the main price
      const mainPriceElement = option.querySelector('.recharge-buy-box__option-price');
      priceToShow = mainPriceElement?.textContent || '';
    }

    // Update main price to show just the number
    if (priceToShow) {
      priceElement.textContent = priceToShow;
    }

    // Update per-unit text to just say "per bottle" asdf
    if (perUnitElement) {
      perUnitElement.textContent = `per ${this.unitType}`;
    }

    // Show/hide BEST DEAL label
    if (bestDealLabel) {
      const isBestDeal = this.isBestDeal(option);
      if (isBestDeal) {
        bestDealLabel.textContent = 'BEST DEAL';
        bestDealLabel.classList.add('best-deal__label--visible');
      } else {
        bestDealLabel.textContent = '';
        bestDealLabel.classList.remove('best-deal__label--visible');
      }
    }

    // Fade in the pricing column now that we have values
    requestAnimationFrame(() => {
      pricingColumn.classList.add('product-heading__pricing-column--loaded');
    });
  }

  /**
   * Determine if an option is the best deal (highest savings)
   */
  isBestDeal(option) {
    // One-time purchase is never the best deal
    if (option.dataset.purchaseType === 'onetime') {
      return false;
    }

    // Get savings text from the option
    const savingsText = option.querySelector('.recharge-buy-box__option-savings')?.textContent;
    if (!savingsText) return false;

    // Extract savings percentage
    const currentSavings = parseInt(savingsText.match(/\d+/)?.[0] || '0');

    // Find max savings across all subscription options
    let maxSavings = 0;
    this.optionButtons.forEach(btn => {
      if (btn.dataset.purchaseType === 'subscription') {
        const btnSavingsText = btn.querySelector('.recharge-buy-box__option-savings')?.textContent;
        const btnSavings = parseInt(btnSavingsText?.match(/\d+/)?.[0] || '0');
        if (btnSavings > maxSavings) {
          maxSavings = btnSavings;
        }
      }
    });

    return currentSavings === maxSavings && maxSavings > 0;
  }

  /**
   * Update URL query parameter with selected plan
   */
  updateUrlParameter(option) {
    const purchaseType = option.dataset.purchaseType;
    let planValue;

    if (purchaseType === 'subscription') {
      planValue = option.dataset.sellingPlanId;
    } else {
      planValue = 'onetime';
    }

    // Update URL without reloading page
    const url = new URL(window.location);
    url.searchParams.set('plan', planValue);
    window.history.replaceState({}, '', url);
  }


  /**
   * Pluralize a unit type name (e.g. "box" → "boxes", "pouch" → "pouches")
   */
  pluralizeUnit(unit) {
    const lower = unit.toLowerCase();
    if (lower === 'box' || lower === 'pouch') return unit + 'es';
    return unit + 's';
  }

  /**
   * Format price in cents to currency string (without .00)
   */
  formatPrice(priceInCents) {
    const price = parseInt(priceInCents) / 100;
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
    return formatted;
  }

  /**
   * Sync selection to Recharge widget
   */
  syncToRechargeWidget(purchaseType, sellingPlanId = null) {

    if (!this.rechargeWidget || !this.rechargeWidget.shadowRoot) {
      console.error('[RechargeBuyBox] Cannot sync - widget or shadow root not available');
      return;
    }

    try {
      const shadowRoot = this.rechargeWidget.shadowRoot;

      if (purchaseType === 'subscription') {
        // Click subscription radio in Recharge widget
        const subscriptionRadio = shadowRoot.querySelector('input[name="purchaseOption"][value="subscription"]');

        if (subscriptionRadio && !subscriptionRadio.checked) {
          subscriptionRadio.click();
        }

        // Select the specific plan
        if (sellingPlanId) {
          this.selectRechargeSellingPlan(sellingPlanId);
        }
      } else {
        // Click one-time radio in Recharge widget
        const onetimeRadio = shadowRoot.querySelector('input[name="purchaseOption"][value="onetime"]');

        if (onetimeRadio && !onetimeRadio.checked) {
          onetimeRadio.click();
        }
      }
    } catch (error) {
      console.error('[RechargeBuyBox] Error syncing to Recharge widget:', error);
    }
  }

  /**
   * Select a specific selling plan in the Recharge widget
   */
  selectRechargeSellingPlan(sellingPlanId) {
    if (!this.rechargeWidget || !this.rechargeWidget.shadowRoot) return;

    try {
      const shadowRoot = this.rechargeWidget.shadowRoot;
      const sellingPlansEl = shadowRoot.querySelector('rc-selling-plans');

      if (sellingPlansEl && sellingPlansEl.shadowRoot) {
        const planRadio = sellingPlansEl.shadowRoot.querySelector(
          `input[name="rc_plan"][value="${sellingPlanId}"]`
        );

        if (planRadio && !planRadio.checked) {
          planRadio.click();
        }
      }
    } catch (error) {
      console.error('Error selecting Recharge selling plan:', error);
    }
  }

  /**
   * Observe changes in the Recharge widget and sync back to buy box
   */
  observeRechargeWidget() {
    if (!this.rechargeWidget || !this.rechargeWidget.shadowRoot) return;

    try {
      const shadowRoot = this.rechargeWidget.shadowRoot;

      // Listen for purchase type changes (one-time vs subscription)
      const purchaseTypeRadios = shadowRoot.querySelectorAll('input[name="purchaseOption"]');
      purchaseTypeRadios.forEach((radio) => {
        radio.addEventListener('change', (e) => {
          this.syncFromRechargeWidget(e.target.value);
        });
      });

      // Listen for selling plan changes
      const sellingPlansEl = shadowRoot.querySelector('rc-selling-plans');
      if (sellingPlansEl && sellingPlansEl.shadowRoot) {
        const planRadios = sellingPlansEl.shadowRoot.querySelectorAll('input[name="rc_plan"]');
        planRadios.forEach((radio) => {
          radio.addEventListener('change', (e) => {
            this.syncSellingPlanFromRecharge(e.target.value);
          });
        });
      }
    } catch (error) {
      console.error('[RechargeBuyBox] Error observing Recharge widget:', error);
    }
  }

  /**
   * Observe variant changes and update buy box accordingly
   */
  observeVariantChanges() {
    // Listen for variant-radios changes
    const variantRadios = document.querySelector('variant-radios');
    if (variantRadios) {
      variantRadios.addEventListener('change', () => {
        this.debouncedVariantChange();
      });
    }

    // Also observe the Recharge widget for attribute changes (variant-id changes)
    if (this.rechargeWidget) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'default-variant-id') {
            this.debouncedVariantChange();
          }
        });
      });

      observer.observe(this.rechargeWidget, {
        attributes: true,
        attributeFilter: ['default-variant-id']
      });
    }
  }

  /**
   * Debounced variant change handler to prevent multiple rapid re-renders
   */
  debouncedVariantChange() {
    // Clear any pending timeout
    if (this.variantChangeTimeout) {
      clearTimeout(this.variantChangeTimeout);
    }

    // Set a new timeout - wait longer to ensure URL and DOM are updated
    this.variantChangeTimeout = setTimeout(() => {
      this.handleVariantChange();
    }, 300);
  }

  /**
   * Handle variant changes - re-render options with new variant data
   */
  async handleVariantChange() {
    // Prevent re-entry
    if (this.isHandlingVariantChange) {
      return;
    }

    this.isHandlingVariantChange = true;

    try {
      // Wait a bit for the DOM to settle
      await new Promise(resolve => setTimeout(resolve, 100));

      // Get the new variant ID
      const newVariantId = this.getCurrentVariantId();

      // Check if variant actually changed (compare as strings)
      if (newVariantId && newVariantId.toString() === this.lastRenderedVariantId) {
        return;
      }

      // Store currently selected option type
      const currentlySelected = this.querySelector('.recharge-buy-box__option--active');
      const currentPurchaseType = currentlySelected?.dataset.purchaseType;
      const currentSellingPlanId = currentlySelected?.dataset.sellingPlanId;

      // Re-render options with new variant
      this.renderSubscriptionOptions(newVariantId);

      // Re-query option buttons
      this.optionButtons = this.querySelectorAll('.recharge-buy-box__option');

      // Re-setup event listeners
      this.setupEventListeners();

      // Try to maintain selection
      let optionToSelect = null;

      if (currentPurchaseType === 'subscription' && currentSellingPlanId) {
        // Try to select same selling plan
        optionToSelect = this.querySelector(`[data-selling-plan-id="${currentSellingPlanId}"]`);
      } else if (currentPurchaseType === 'onetime') {
        // Select one-time option
        optionToSelect = this.querySelector('[data-purchase-type="onetime"]');
      }

      // Default to first option if previous selection not found
      if (!optionToSelect) {
        optionToSelect = this.querySelector('.recharge-buy-box__option');
      }

      if (optionToSelect) {
        optionToSelect.classList.add('recharge-buy-box__option--active');

        // Update product heading pricing after variant change
        this.updateProductHeadingPricing(optionToSelect);

        // Sync with Recharge widget (only if widget exists)
        if (this.rechargeWidget) {
          const purchaseType = optionToSelect.dataset.purchaseType;
          if (purchaseType === 'subscription') {
            const sellingPlanId = optionToSelect.dataset.sellingPlanId;
            this.syncToRechargeWidget('subscription', sellingPlanId);
          } else {
            this.syncToRechargeWidget('onetime');
          }
        }
      }
    } catch (error) {
      console.error('[RechargeBuyBox] Error handling variant change:', error);
    } finally {
      this.isHandlingVariantChange = false;
    }
  }

  /**
   * Sync selection from Recharge widget to buy box
   */
  syncFromRechargeWidget(purchaseType) {
    if (purchaseType === 'subscription') {
      const subscriptionTrigger = this.querySelector('[data-option-type="subscription"]');
      if (subscriptionTrigger) {
        this.selectTrigger(subscriptionTrigger);
      }
    } else {
      const onetimeTrigger = this.querySelector('[data-option-type="onetime"]');
      if (onetimeTrigger) {
        this.selectTrigger(onetimeTrigger);
      }
    }
  }

  /**
   * Sync selling plan selection from Recharge widget to buy box
   */
  syncSellingPlanFromRecharge(sellingPlanId) {
    const option = this.querySelector(`[data-selling-plan-id="${sellingPlanId}"]`);
    if (option) {
      this.selectOption(option);
    }
  }
}

// Register custom element
try {
  customElements.define('recharge-buy-box', RechargeBuyBox);
} catch (error) {
  console.error('[RechargeBuyBox] ✗ Failed to register custom element:', error);
}
